import React, { useState, useEffect, useRef } from "react";
import {
  sendChatMessage,
  getChatSuggestions,
  buildChatHistory,
} from "../../services/chatbotService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { contactInfo, welcomeMessage } from "../../utils/customerSupportData";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../auth/AuthContext";
import ChatProductCards from "../chatbot/ChatProductCards";

// Robot Icon — Fixed waving animation
const RobotIcon = ({ isWaving, lookDir, isListening = false, className = "" }) => {
  const [eyeBlink, setEyeBlink] = useState(false);
  const [eyeDirection, setEyeDirection] = useState({ x: 0, y: 0 });
  const [expression, setExpression] = useState("neutral");

  // Random blinking
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setEyeBlink(true);
      setTimeout(() => setEyeBlink(false), 150);
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(blinkInterval);
  }, []);

  // Update eye direction based on scroll
  useEffect(() => {
    const eyeX = lookDir === "left" ? -2 : lookDir === "right" ? 2 : 0;
    const eyeY = lookDir === "up" ? -2 : lookDir === "down" ? 2 : 0;
    setEyeDirection({ x: eyeX, y: eyeY });
  }, [lookDir]);

  // Random expression changes
  useEffect(() => {
    const expressionInterval = setInterval(() => {
      const expressions = ["neutral", "happy", "thinking"];
      const randomExp = expressions[Math.floor(Math.random() * expressions.length)];
      setExpression(randomExp);
    }, 8000);
    return () => clearInterval(expressionInterval);
  }, []);

  return (
    <svg
      viewBox="0 0 140 170"
      className={className}
      aria-hidden="true"
      style={{ filter: "drop-shadow(0 10px 20px rgba(15, 23, 42, 0.3))" }}
    >
      <defs>
        <linearGradient id="robotBody" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#CFE0F0" />
          <stop offset="55%" stopColor="#B7D0E8" />
          <stop offset="100%" stopColor="#9FBEDD" />
        </linearGradient>
        <linearGradient id="robotVisor" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#173238" />
          <stop offset="100%" stopColor="#0A1B1F" />
        </linearGradient>
        <radialGradient id="eyeGreen" cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#8BF29B" />
          <stop offset="55%" stopColor="#3DDC63" />
          <stop offset="100%" stopColor="#1FA84A" />
        </radialGradient>
        <radialGradient id="eyeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#4FE07A" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#4FE07A" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="darkLimb" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1D3A40" />
          <stop offset="100%" stopColor="#0E2226" />
        </linearGradient>
        <linearGradient id="strap" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#5B8FC7" />
          <stop offset="100%" stopColor="#4779AC" />
        </linearGradient>
      </defs>

      <style>{`
        @keyframes robotFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        .robot-floating { animation: robotFloat 4s ease-in-out infinite; }

        @keyframes shadowPulse {
          0%, 100% { transform: scaleX(1); opacity: 0.3; }
          50% { transform: scaleX(0.8); opacity: 0.15; }
        }
        .robot-shadow { animation: shadowPulse 4s ease-in-out infinite; }

        @keyframes corePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        .core-pulse { animation: corePulse 2.5s ease-in-out infinite; }

        @keyframes antennaGlow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .antenna-glow { animation: antennaGlow 1.8s ease-in-out infinite; }

        .eyes-group {
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          transform-origin: 70px 47px;
        }

        @keyframes blink {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(0.5); }
        }
        .eye-blink { animation: blink 0.15s ease-in-out; transform-origin: center; }

        /* ===== Human-like Wave Arm (upper-arm -> forearm -> hand, nested joints) ===== */
        .arm-wrapper {
          transform-origin: 100px 90px; /* shoulder */
        }

        .upper-arm {
          transform-origin: 100px 90px; /* shoulder */
        }

        .forearm {
          transform-origin: 108px 112px; /* elbow */
        }

        .hand {
          transform-origin: 102px 136px; /* wrist */
        }

        /* WAVING STATE */
        .arm-wrapper.waving {
          animation: raiseArm 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .arm-wrapper.waving .forearm {
          animation: bendAtElbow 0.45s ease-out 0.15s forwards;
        }

        .arm-wrapper.waving .hand {
          animation: waveHand 0.4s ease-in-out 0.55s infinite alternate;
        }

        /* RETURNING STATE */
        .arm-wrapper.returning {
          animation: lowerArm 0.4s ease-in forwards;
        }

        .arm-wrapper.returning .forearm {
          animation: straightenElbow 0.35s ease-in forwards;
        }

        .arm-wrapper.returning .hand {
          animation: stopWave 0.25s ease-in forwards;
        }

        /* KEYFRAMES */

        /* shoulder lifts the whole arm up and out to the side (kept slightly
           short of vertical so the hand clears the head instead of tucking behind it) */
        @keyframes raiseArm {
          0%   { transform: rotate(0deg); }
          60%  { transform: rotate(-80deg); }
          100% { transform: rotate(-74deg); }
        }

        /* elbow bends so the forearm swings up, forming the inverted-L (Γ) shape,
           angle reduced a bit so the wrist stays out to the side of the head */
        @keyframes bendAtElbow {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(-102deg); }
        }

        /* wrist waves side to side with a bigger, clearly visible swing */
        @keyframes waveHand {
          0%   { transform: rotate(-38deg); }
          100% { transform: rotate(38deg); }
        }

        @keyframes lowerArm {
          0%   { transform: rotate(-74deg); }
          100% { transform: rotate(0deg); }
        }

        @keyframes straightenElbow {
          0%   { transform: rotate(-102deg); }
          100% { transform: rotate(0deg); }
        }

        @keyframes stopWave {
          0%   { transform: rotate(38deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>

      {/* Shadow */}
      <ellipse className="robot-shadow" cx="70" cy="163" rx="30" ry="5" fill="rgba(0,0,0,0.2)" />

      <g className="robot-floating">
        {/* Feet */}
        <g>
          <path d="M 48 148 h 22 a 6 6 0 0 1 6 6 v 4 a 4 4 0 0 1 -4 4 h -26 a 4 4 0 0 1 -3 -6 z" fill="url(#robotBody)" stroke="#8FA9C4" strokeWidth="1.5" />
          <path d="M 64 148 h 22 a 6 6 0 0 1 6 6 v 4 a 4 4 0 0 1 -4 4 h -26 a 4 4 0 0 1 -3 -6 z" fill="url(#robotBody)" stroke="#8FA9C4" strokeWidth="1.5" />
        </g>

        {/* Legs */}
        <g>
          <rect x="52" y="112" width="16" height="40" rx="7" fill="url(#robotBody)" stroke="#8FA9C4" strokeWidth="1.5" />
          <rect x="72" y="112" width="16" height="40" rx="7" fill="url(#robotBody)" stroke="#8FA9C4" strokeWidth="1.5" />
          <line x1="53" y1="132" x2="67" y2="128" stroke="#7A98B8" strokeWidth="2" strokeLinecap="round" />
          <line x1="73" y1="128" x2="87" y2="132" stroke="#7A98B8" strokeWidth="2" strokeLinecap="round" />
        </g>

        {/* Hip / pelvis piece */}
        <path d="M 58 104 h 24 a 6 6 0 0 1 6 6 v 4 a 6 6 0 0 1 -6 6 h -24 a 6 6 0 0 1 -6 -6 v -4 a 6 6 0 0 1 6 -6 z" fill="url(#darkLimb)" />

        {/* Right Arm (rests down) */}
        <g>
          <path d="M 42 92 L 34 112 L 40 136" stroke="url(#robotBody)" strokeWidth="11" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <ellipse cx="38" cy="139" rx="7" ry="6" fill="url(#darkLimb)" />
        </g>

        {/* ===== Left Arm — Human Wave (Upper arm -> Forearm -> Hand, properly nested) ===== */}
        <g className={`arm-wrapper ${isWaving ? "waving" : ""}`}>
          {/* UPPER ARM - rotates from shoulder */}
          <g className="upper-arm">
            {/* Upper arm line from shoulder to elbow */}
            <path
              d="M 100 90 L 108 112"
              stroke="url(#robotBody)"
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
            />
            {/* Shoulder joint */}
            <circle cx="100" cy="90" r="7" fill="url(#darkLimb)" />
            {/* Elbow joint - stays connected to upper arm */}
            <circle cx="108" cy="112" r="6" fill="url(#darkLimb)" />

            {/* FOREARM - nested inside upper-arm so it inherits the shoulder rotation,
                then bends further from the elbow. This keeps it physically attached. */}
            <g className="forearm">
              {/* Forearm line from elbow to wrist */}
              <path
                d="M 108 112 L 102 136"
                stroke="url(#robotBody)"
                strokeWidth="11"
                fill="none"
                strokeLinecap="round"
              />

              {/* HAND - nested inside forearm so it stays glued to the wrist
                  and only rotates about the wrist joint to wave */}
              <g className="hand">
                {/* Palm - rounded circle */}
                <circle cx="102" cy="139" r="8" fill="url(#darkLimb)" />
                {/* Simple fingers - small circles on palm */}
                <circle cx="98" cy="143" r="3.5" fill="url(#darkLimb)" />
                <circle cx="102" cy="145" r="3.5" fill="url(#darkLimb)" />
                <circle cx="106" cy="143" r="3.5" fill="url(#darkLimb)" />
              </g>
            </g>
          </g>
        </g>

        {/* Shoulder joints */}
        <g>
          <ellipse cx="42" cy="90" rx="7" ry="6" fill="url(#darkLimb)" />
        </g>

        {/* Torso */}
        <g>
          <rect x="40" y="78" width="60" height="56" rx="26" fill="url(#robotBody)" stroke="#8FA9C4" strokeWidth="1.5" />
          {/* Straps */}
          <path d="M 55 84 L 70 118" stroke="url(#strap)" strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M 85 84 L 70 118" stroke="url(#strap)" strokeWidth="4" fill="none" strokeLinecap="round" />
          {/* Core */}
          <circle className="core-pulse" cx="70" cy="112" r="6" fill="url(#eyeGreen)" />
          <circle cx="70" cy="112" r="10" fill="url(#eyeGlow)" />
        </g>

        {/* Neck */}
        <rect x="60" y="66" width="20" height="16" rx="6" fill="url(#darkLimb)" />

        {/* Antenna */}
        <g>
          <path d="M 70 26 L 65 20 L 75 14 L 70 8" stroke="#12262A" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <circle className="antenna-glow" cx="70" cy="6" r="6" fill="#12262A" />
        </g>

        {/* Head */}
        <g>
          <rect x="30" y="14" width="80" height="60" rx="30" fill="url(#robotBody)" stroke="#8FA9C4" strokeWidth="1.5" />

          {/* Visor */}
          <rect x="40" y="28" width="60" height="42" rx="21" fill="url(#robotVisor)" />

          {/* Eyes + mouth */}
          <g className="eyes-group" style={{ transform: `translate(${eyeDirection.x * 0.5}px, ${eyeDirection.y * 0.5}px)` }}>
            <g className={eyeBlink ? "eye-blink" : ""}>
              <circle cx="58" cy="46" r="8" fill="url(#eyeGreen)" />
              <circle cx="58" cy="46" r="11" fill="url(#eyeGlow)" />
              <circle cx="55.5" cy="43" r="2" fill="white" opacity="0.85" />
            </g>
            <g className={eyeBlink ? "eye-blink" : ""}>
              <circle cx="82" cy="46" r="8" fill="url(#eyeGreen)" />
              <circle cx="82" cy="46" r="11" fill="url(#eyeGlow)" />
              <circle cx="79.5" cy="43" r="2" fill="white" opacity="0.85" />
            </g>

            {/* Mouth - happy when waving */}
            {isWaving ? (
              <path d="M 56 60 Q 70 78 84 60 Q 70 70 56 60 Z" fill="url(#eyeGreen)" />
            ) : expression === "happy" ? (
              <path d="M 56 60 Q 70 76 84 60 Q 70 68 56 60 Z" fill="url(#eyeGreen)" />
            ) : expression === "thinking" ? (
              <circle cx="70" cy="62" r="3" fill="url(#eyeGreen)" />
            ) : isListening ? (
              <rect x="60" y="58" width="20" height="8" rx="4" fill="url(#eyeGreen)" />
            ) : (
              <path d="M 58 60 Q 70 70 82 60 Q 70 67 58 60 Z" fill="url(#eyeGreen)" />
            )}
          </g>
        </g>
      </g>
    </svg>
  );
};

// Main Chatbot Component
const CustomerSupportChatbot = ({ isProductDetailsPage = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [bottomOffset, setBottomOffset] = useState("1.5rem");
  const messagesEndRef = useRef(null);
  const { setIsChatbotOpen } = useChat();
  const { user, isGuest } = useAuth();
  const isAuthenticated = Boolean(user && !isGuest);

  // Robot animation states
  const [isWaving, setIsWaving] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [lookDir, setLookDir] = useState("center");
  const lastScrollYRef = useRef(0);
  const lookTimeoutRef = useRef(null);
  const waveTimeoutRef = useRef(null);
  const listenTimeoutRef = useRef(null);

  // Trigger wave animation
  const triggerWave = () => {
    if (isWaving) {
      // Reset to replay animation
      setIsWaving(false);
      setTimeout(() => {
        setIsWaving(true);
        if (waveTimeoutRef.current) clearTimeout(waveTimeoutRef.current);
        waveTimeoutRef.current = setTimeout(() => {
          setIsWaving(false);
        }, 2000);
      }, 50);
      return;
    }

    setIsWaving(true);
    if (waveTimeoutRef.current) clearTimeout(waveTimeoutRef.current);
    waveTimeoutRef.current = setTimeout(() => {
      setIsWaving(false);
    }, 2000);
  };

  // Initial wave on load
  useEffect(() => {
    const loadTimer = setTimeout(() => triggerWave(), 800);
    return () => {
      clearTimeout(loadTimer);
      if (waveTimeoutRef.current) clearTimeout(waveTimeoutRef.current);
    };
  }, []);

  // Scroll detection
  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const isMobileView = () => window.matchMedia("(max-width: 767px)").matches;

    const updateBottomOffset = () => {
      if (!isMobileView()) {
        setBottomOffset("0.5rem");
        return;
      }

      const nav = document.querySelector("[data-mobile-bottom-nav]");
      if (!nav) {
        setBottomOffset("0.5rem");
        return;
      }

      const navStyle = window.getComputedStyle(nav);
      if (navStyle.display === "none" || navStyle.visibility === "hidden") {
        setBottomOffset("0.5rem");
        return;
      }

      const isVisible = nav.getAttribute("data-mobile-bottom-nav") === "visible";
      setBottomOffset(isVisible ? "4rem" : "0.5rem");
    };

    const handleScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollYRef.current;

      if (Math.abs(diff) > 5) {
        setLookDir(diff > 0 ? "down" : "up");

        if (lookTimeoutRef.current) clearTimeout(lookTimeoutRef.current);
        lookTimeoutRef.current = setTimeout(() => {
          setLookDir("center");
        }, 800);
      }

      lastScrollYRef.current = currentY;
      updateBottomOffset();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateBottomOffset);
    updateBottomOffset();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateBottomOffset);
      if (lookTimeoutRef.current) clearTimeout(lookTimeoutRef.current);
    };
  }, []);

  // Listening animation
  useEffect(() => {
    if (isTyping) {
      setIsListening(true);
      if (listenTimeoutRef.current) clearTimeout(listenTimeoutRef.current);
    } else {
      listenTimeoutRef.current = setTimeout(() => {
        setIsListening(false);
      }, 1000);
    }
    return () => {
      if (listenTimeoutRef.current) clearTimeout(listenTimeoutRef.current);
    };
  }, [isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleToggleChat = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    setIsChatbotOpen(newState);
    if (!newState) {
      setIsListening(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const suggestionsLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    suggestionsLoadedRef.current = false;
    const loadSuggestions = async () => {
      try {
        const fetchedSuggestions = await getChatSuggestions();
        setSuggestions(fetchedSuggestions);
        suggestionsLoadedRef.current = true;
      } catch (error) {
        setSuggestions([
          "Find cameras near me",
          "How does renting work?",
          "What's the cancellation policy?",
          "Show affordable bikes to rent",
        ]);
      }
    };
    loadSuggestions();
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          type: "bot",
          text: welcomeMessage,
          timestamp: new Date(),
        },
      ]);
      setTimeout(() => triggerWave(), 500);
    }
  }, [isOpen]);

  const handleSend = async (text = inputValue) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      type: "user",
      text: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setShowSuggestions(false);
    setIsLoading(true);
    setIsTyping(true);

    try {
      const history = buildChatHistory(messages);
      const response = await sendChatMessage(text, history);

      const botMessage = {
        type: "bot",
        text: response.response,
        timestamp: new Date(),
        products: response.products || [],
      };

      setMessages((prev) => [...prev, botMessage]);
      setTimeout(() => triggerWave(), 300);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = {
        type: "bot",
        text: "I apologize, but I'm having trouble processing your request right now. Please try again or contact our support team at " +
          contactInfo.email,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSend(suggestion);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      <button
        onClick={handleToggleChat}
        onMouseEnter={triggerWave}
        style={{ bottom: bottomOffset }}
        className={`fixed right-2 sm:right-2 z-50 flex items-center justify-center transition-all duration-300 ${
          isOpen
            ? "w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] text-white shadow-lg hover:shadow-xl hover:scale-105 hover:from-blue-700 hover:to-blue-800"
            : "w-20 h-20 sm:w-24 sm:h-24 bg-transparent hover:scale-110 transition-transform duration-300"
        }`}
        aria-label="Toggle chat"
      >
        {isOpen ? (
          <svg
            className="w-6 h-6 sm:w-7 sm:h-7"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <RobotIcon
            isWaving={isWaving}
            lookDir={lookDir}
            isListening={isListening}
            className="w-16 h-16 sm:w-20 sm:h-20"
          />
        )}
        
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-20 sm:right-6 z-50 sm:w-96 bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border-0 sm:border border-gray-200 sm:min-h-[80vh] sm:max-h-[80vh] md:min-h-[80vh] md:max-h-[80vh] lg:min-h-[80vh] lg:max-h-[80vh] h-full sm:h-auto dark:bg-gray-800 dark:border-gray-700">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white p-3 sm:p-4 flex items-center justify-between shadow-md">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                <img
                  src="/logo.png"
                  alt="AkkoRent"
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-base sm:text-lg flex items-center gap-2">
                  AkkoRent AI Assistant
                </h3>
                <p className="text-xs text-blue-100">
                  {isTyping
                    ? "Thinking..."
                    : isAuthenticated
                      ? "Signed in · Ready to help"
                      : "Online · Search, rent & get help"}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleChat}
              className="text-white hover:bg-white/20 rounded-full p-1.5 sm:p-1 transition-colors flex-shrink-0"
              aria-label="Close chat"
            >
              <svg
                className="w-5 h-5 sm:w-5 sm:h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gradient-to-b from-slate-50 via-white to-slate-100 dark:from-gray-900 dark:to-gray-800">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex flex-col ${message.type === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[92%] sm:max-w-[85%] rounded-2xl px-3 py-2 sm:px-4 ${
                    message.type === "user"
                      ? "bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white rounded-br-md shadow-md"
                      : message.isError
                        ? "bg-red-50 text-red-800 rounded-bl-md shadow-sm border border-red-200 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200"
                        : "bg-white text-gray-800 rounded-bl-md shadow-sm border border-slate-200/80 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  }`}
                >
                  <div className="text-xs sm:text-sm leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => (
                          <p className="mb-1 last:mb-0" {...props} />
                        ),
                        ul: ({ node, ...props }) => (
                          <ul className="list-disc ml-4 mb-2" {...props} />
                        ),
                        ol: ({ node, ...props }) => (
                          <ol className="list-decimal ml-4 mb-2" {...props} />
                        ),
                        li: ({ node, ...props }) => (
                          <li className="mb-0.5" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                          <a
                            className="underline hover:text-blue-200"
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          />
                        ),
                        blockquote: ({ node, ...props }) => (
                          <blockquote
                            className="border-l-2 border-gray-300 pl-2 italic my-1"
                            {...props}
                          />
                        ),
                        strong: ({ node, ...props }) => (
                          <strong
                            className="font-bold !font-bold text-gray-900 dark:text-white"
                            {...props}
                          />
                        ),
                        code: ({ node, inline, ...props }) =>
                          inline ? (
                            <code
                              className="bg-black/10 rounded px-1 py-0.5 text-xs font-mono"
                              {...props}
                            />
                          ) : (
                            <pre className="bg-black/10 rounded p-2 overflow-x-auto my-1 text-xs font-mono">
                              <code {...props} />
                            </pre>
                          ),
                        table: ({ node, ...props }) => (
                          <div className="my-2 -mx-1 overflow-x-auto rounded-lg border border-slate-200/80 dark:border-slate-600">
                            <table className="min-w-full text-left text-[11px] sm:text-xs" {...props} />
                          </div>
                        ),
                        thead: ({ node, ...props }) => (
                          <thead className="bg-slate-50 dark:bg-slate-800/80" {...props} />
                        ),
                        th: ({ node, ...props }) => (
                          <th className="whitespace-nowrap px-2.5 py-1.5 font-semibold text-slate-700 dark:text-slate-200" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                          <td className="whitespace-nowrap px-2.5 py-1.5 text-slate-600 dark:text-slate-300" {...props} />
                        ),
                        tr: ({ node, ...props }) => (
                          <tr className="border-t border-slate-100 first:border-t-0 dark:border-slate-700" {...props} />
                        ),
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>
                  <p
                    className={`text-xs mt-1 ${
                      message.type === "user"
                        ? "text-blue-100"
                        : "text-gray-400 dark:text-gray-300"
                    }`}
                  >
                    {formatTime(message.timestamp)}
                  </p>
                </div>
                {message.type === "bot" && message.products?.length > 0 && (
                  <div className="w-full max-w-full pr-1">
                    <ChatProductCards products={message.products} />
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm border border-gray-200 dark:bg-gray-700 dark:border-gray-600">
                  <div className="flex space-x-2">
                    <div
                      className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                      style={{ animationDelay: "0ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />

            {showSuggestions && messages.length === 1 && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-gray-500 dark:text-gray-300 font-medium flex items-center gap-1">
                  <span>💡</span> Quick questions:
                </p>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      disabled={isLoading}
                      className="bg-blue-50 text-[#2563EB] text-xs px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-full border border-blue-200 hover:bg-[#2563EB] hover:text-white transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-blue-300 dark:border-gray-600 dark:hover:bg-gray-600 transform hover:scale-105 duration-200"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Helpline Info */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-t border-yellow-100 px-3 py-2 sm:px-4 dark:from-yellow-900/20 dark:to-orange-900/20 dark:border-yellow-800">
            <div className="flex items-start space-x-2">
              <svg
                className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0 dark:text-yellow-300"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-xs text-yellow-800 dark:text-yellow-200">
                <p className="font-semibold">Need human support?</p>
                <p className="break-all">
                  Email:{" "}
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="underline hover:text-yellow-900 dark:hover:text-yellow-100 font-medium"
                  >
                    {contactInfo.email}
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Input Area */}
          <div className="p-3 sm:p-4 bg-white border-t border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="flex items-center space-x-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={isAuthenticated ? "Ask about listings, wishlist, account..." : "Search rentals, policies, listings..."}
                disabled={isLoading}
                className="flex-1 px-3 py-2.5 sm:px-4 sm:py-3 border-2 border-gray-300 rounded-full outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-blue-200 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-300 dark:focus:border-[#2563EB]"
              />
              <button
                type="submit"
                disabled={isLoading || !inputValue.trim()}
                className="bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center hover:from-blue-700 hover:to-blue-800 transition-all shadow-md flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 duration-200"
                aria-label="Send message"
              >
                {isLoading ? (
                  <svg
                    className="animate-spin w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 sm:w-5 sm:h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerSupportChatbot;